/**
 * File: articles.ts
 *
 * Runtime client for the articles. They are published in Skapedia and read
 * through the site's one Skapedia instance (src/code/skapedia.ts), so nothing
 * article-related is bundled into the app or kept in this repository:
 *   - the list comes in pages (`skapedia.listArticles(more)`). A page is
 *     fetched when something needs it and kept here, so the list view, the
 *     news banner, the landing page and the article view share what is loaded.
 *   - an article's markdown is fetched on demand (`skapedia.getArticle()`), so
 *     the payload tracks what is viewed, not how many articles exist.
 *
 * The metadata shape and what the site reads from the markdown live in
 * articles-meta.ts.
 */

import { marked } from "marked";
import { isOtherSite, redirectOf } from "./article-redirects";
import { skapedia } from "./skapedia";
import {
    ARTICLE_MARKED_OPTIONS,
    articleBodyMarkdown,
    fromSkapedia,
    withBody,
    type ArticleMeta,
} from "./articles-meta";

export type Article = ArticleMeta;

/** The articles loaded so far, newest first, and whether that is all of them. */
export interface ArticleList {
    articles: Article[];
    end: boolean;
}

/** An article with its body: what the article view shows and offers as a download. */
export interface ArticleBody {
    article: Article; // with the overrides its markdown declares, see articles-meta.ts
    markdown: string; // the article as published
    html: string; // the body as the page shows it
}

/** How long an article stays in the news banner after its date (30 days). */
const BANNER_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

// The list as loaded so far, in the order Skapedia gives it (newest first).
const loaded: Article[] = [];
const slugs = new Set<string>();
// What the next `listArticles()` takes: null asks for the first page.
let nextPage: number | null = null;
let ended = false;
// One page is fetched at a time and the fetch is shared, so two views that ask
// together get the same page once. A failed fetch is forgotten, so a later
// call asks for that page again.
let pageLoad: Promise<void> | null = null;

function loadNextPage(): Promise<void> {
    if (ended) return Promise.resolve();
    if (!pageLoad) {
        const asked = nextPage;
        pageLoad = skapedia
            .listArticles(asked)
            .then((page) => {
                for (const item of page.items) {
                    const article = fromSkapedia(item);
                    if (!article || slugs.has(article.slug)) continue;
                    slugs.add(article.slug);
                    loaded.push(article);
                }
                // `more` is the next page, or null after the last one. The same
                // page twice would never end, so that ends the list too.
                ended = page.more === null || page.more === undefined || page.more === asked;
                if (!ended) nextPage = page.more;
            })
            .finally(() => {
                pageLoad = null;
            });
    }
    return pageLoad;
}

const snapshot = (): ArticleList => ({ articles: loaded.slice(), end: ended });

/**
 * The articles loaded so far, newest first. When nothing is loaded yet the
 * first page is fetched. A page can come back with nothing to show (Skapedia
 * leaves out articles dated after today), so this reads on until there is
 * something to show or the list ends.
 */
export async function getArticles(): Promise<ArticleList> {
    while (!loaded.length && !ended) await loadNextPage();
    return snapshot();
}

/** Fetch the next page, then the same answer as `getArticles()`. */
export async function getMoreArticles(): Promise<ArticleList> {
    await loadNextPage();
    return snapshot();
}

/**
 * A single article's metadata by slug, or `null` when unknown. Looks at what is
 * loaded first, then reads further pages until the article turns up or the
 * list ends.
 */
export async function getArticle(slug: string): Promise<Article | null> {
    for (;;) {
        const hit = loaded.find((a) => a.slug === slug);
        if (hit) return hit;
        if (ended) return null;
        await loadNextPage();
    }
}

/**
 * Where to send a visitor who opened an article's old address (see
 * article-redirects.ts): a path on this site, or a full address on another
 * site. `null` when `slug` is not an old address, or when the article it
 * stands for is not in Skapedia's list, so nobody is sent to a page that says
 * "not found".
 */
export async function getMovedArticle(slug: string): Promise<string | null> {
    const target = redirectOf(slug);
    if (!target) return null;
    if (isOtherSite(target)) return target;
    return (await getArticle(target)) ? `/articles/${target}` : null;
}

/**
 * The single most recent article Skapedia flags as news whose date is within
 * the last 30 days, or `null`. Drives the home-page news banner. The newest
 * articles are on the first page, so that is all it reads.
 */
export async function getBannerArticle(): Promise<Article | null> {
    const { articles } = await getArticles();
    const newest = articles.find((a) => a.banner && a.dateSort);
    if (!newest) return null;
    return Date.now() - newest.dateSort < BANNER_WINDOW_MS ? newest : null;
}

/**
 * Fetch an article's markdown through Skapedia and render it to HTML. Skapedia
 * keeps what it has fetched, so opening an article again costs no request.
 *
 * The markdown is asked for, not Skapedia's own HTML (`type: "html"`): that
 * HTML keeps the title line as an h1, and the page shows the title in its
 * header. So the title is dropped here and the body is rendered with the
 * options the build-time prerender uses too (articles-meta.ts), which keeps
 * the static copy and this view the same HTML. Standard article markdown
 * semantics (blank line = paragraph), without the `breaks: true` option.
 */
export async function loadArticleBody(article: Article): Promise<ArticleBody> {
    const markdown = await skapedia.getArticle({ url: article.url });
    return {
        article: withBody(article, markdown),
        markdown,
        html: marked.parse(articleBodyMarkdown(markdown), ARTICLE_MARKED_OPTIONS) as string,
    };
}
